import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { useSlotContent } from '../../hooks/use-slot-content';
import type { UpsellPropsInterface } from './types/upsell.types';
import { UpsellAction } from './upsell-action';
import { UpsellBody } from './upsell-body';
import { UpsellCloseButton } from './upsell-close-button';
import { UpsellContent } from './upsell-content';
import { UpsellHeader } from './upsell-header';
import { UpsellLegal } from './upsell-legal';
import { UpsellMedia } from './upsell-media';
import { UpsellRoot } from './upsell-root';

/**
 * @related ['MediaObject']
 * @since 7.8.0
 */
export const Upsell = forwardRef<HTMLDivElement, UpsellPropsInterface>(
  (props: UpsellPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      body,
      children,
      closeable = true,
      action,
      header,
      media,
      legal,
      ...rest
    } = props;

    const content = useSlotContent({
      children,
      asChild,
      alternativeContent: body,
      template: (childContent) => (
        <>
          <Upsell.Media>{media}</Upsell.Media>
          <Upsell.Content>
            <Upsell.Header>{header}</Upsell.Header>
            <Upsell.Body>{childContent}</Upsell.Body>
            <Upsell.Action>{action}</Upsell.Action>
            {legal ? <Upsell.Legal>{legal}</Upsell.Legal> : null}
          </Upsell.Content>
          {closeable ? <Upsell.CloseButton /> : null}
        </>
      ),
      dependencies: [body, closeable, action, legal, header, media],
    });

    return (
      <Upsell.Root ref={forwardedRef} asChild={asChild} {...rest}>
        {content}
      </Upsell.Root>
    );
  },
) as ForwardRefExoticComponent<UpsellPropsInterface> & {
  Root: typeof UpsellRoot;
  Media: typeof UpsellMedia;
  Content: typeof UpsellContent;
  Header: typeof UpsellHeader;
  Body: typeof UpsellBody;
  Action: typeof UpsellAction;
  Legal: typeof UpsellLegal;
  CloseButton: typeof UpsellCloseButton;
};

Upsell.displayName = 'Upsell';

Upsell.Root = UpsellRoot;
Upsell.Media = UpsellMedia;
Upsell.Content = UpsellContent;
Upsell.Header = UpsellHeader;
Upsell.Body = UpsellBody;
Upsell.Action = UpsellAction;
Upsell.Legal = UpsellLegal;
Upsell.CloseButton = UpsellCloseButton;
